import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Briefcase, 
  Users, 
  MessageSquare, 
  Plus, 
  Edit, 
  Trash2,
  Eye,
  EyeOff,
  AlertCircle,
  ChevronRight,
  Search,
  Filter,
  X,
  CheckCircle,
  Clock,
  XCircle,
  LayoutDashboard,
  LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { jobsApi, contactApi, applicationsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDate } from '../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gita-enterprise/artifacts/18uc5tiv_Global%20IT%20-%20Logo.jpg";

const statusColors = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  closed: 'bg-slate-100 text-slate-700',
  reviewing: 'bg-purple-100 text-purple-700',
  interviewed: 'bg-indigo-100 text-indigo-700',
  hired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

const initialJobForm = {
  title: '',
  department: '',
  location: '',
  employment_type: 'Full-time',
  experience_level: '',
  description: '',
  requirements: [''],
  skills: [''],
  is_urgent: false,
  salary_range: ''
};

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Job form state
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState(initialJobForm);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, submissionsRes, appsRes] = await Promise.all([
        jobsApi.getAll({ active_only: false }),
        contactApi.getSubmissions(),
        applicationsApi.getAll()
      ]);
      setJobs(jobsRes.data);
      setSubmissions(submissionsRes.data);
      setApplications(appsRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching data:', err);
      }
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Job CRUD
  const handleSaveJob = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const jobData = {
        ...jobForm,
        requirements: jobForm.requirements.filter(r => r.trim()),
        skills: jobForm.skills.filter(s => s.trim())
      };

      if (editingJob) {
        await jobsApi.update(editingJob.id, jobData);
        toast.success('Job updated successfully');
      } else {
        await jobsApi.create(jobData);
        toast.success('Job created successfully');
      }
      
      setJobDialogOpen(false);
      setEditingJob(null);
      setJobForm(initialJobForm);
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving job:', err);
      }
      toast.error('Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title,
      department: job.department,
      location: job.location,
      employment_type: job.employment_type,
      experience_level: job.experience_level,
      description: job.description,
      requirements: job.requirements.length > 0 ? job.requirements : [''],
      skills: job.skills.length > 0 ? job.skills : [''],
      is_urgent: job.is_urgent,
      salary_range: job.salary_range || ''
    });
    setJobDialogOpen(true);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await jobsApi.delete(jobId);
      toast.success('Job deleted successfully');
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error deleting job:', err);
      }
      toast.error('Failed to delete job');
    }
  };

  const handleToggleJobActive = async (job) => {
    try {
      await jobsApi.update(job.id, { is_active: !job.is_active });
      toast.success(`Job ${job.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error toggling job:', err);
      }
      toast.error('Failed to update job status');
    }
  };

  // Submission status update
  const handleUpdateSubmissionStatus = async (id, status) => {
    try {
      await contactApi.updateStatus(id, status);
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating status:', err);
      }
      toast.error('Failed to update status');
    }
  };

  // Application status update
  const handleUpdateApplicationStatus = async (id, status) => {
    try {
      await applicationsApi.updateStatus(id, status);
      toast.success('Application status updated');
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating status:', err);
      }
      toast.error('Failed to update status');
    }
  };

  const addArrayField = (field) => {
    setJobForm(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayField = (field, index, value) => {
    setJobForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayField = (field, index) => {
    setJobForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0A2540] text-white sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <img src={LOGO_URL} alt="GITA" className="h-10 w-auto brightness-0 invert" />
            </Link>
            <div className="h-8 w-px bg-white/20" />
            <span className="font-semibold">Admin Dashboard</span>
          </div>
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="back-to-site-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Back to Site
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-100" data-testid="stat-jobs">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Jobs</p>
                <p className="text-3xl font-bold text-[#0A2540]">
                  {jobs.filter(j => j.is_active).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#2563EB]" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-100" data-testid="stat-submissions">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">New Inquiries</p>
                <p className="text-3xl font-bold text-[#0A2540]">
                  {submissions.filter(s => s.status === 'new').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-100" data-testid="stat-applications">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Applications</p>
                <p className="text-3xl font-bold text-[#0A2540]">
                  {applications.filter(a => a.status === 'new').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-slate-100" data-testid="stat-urgent">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Urgent Roles</p>
                <p className="text-3xl font-bold text-[#0A2540]">
                  {jobs.filter(j => j.is_urgent && j.is_active).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6" data-testid="admin-tabs">
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Job Listings
            </TabsTrigger>
            <TabsTrigger value="submissions" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Contact Submissions
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Applications
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="bg-white rounded-xl border border-slate-100">
              <div className="flex items-center justify-between p-6 border-b border-slate-100">
                <h2 className="text-xl font-semibold text-[#0A2540]">Job Listings</h2>
                <Dialog open={jobDialogOpen} onOpenChange={(open) => {
                  setJobDialogOpen(open);
                  if (!open) {
                    setEditingJob(null);
                    setJobForm(initialJobForm);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-job-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Job
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
                      <DialogDescription>
                        Fill in the job details below
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSaveJob} className="space-y-6 mt-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Job Title *</Label>
                          <Input 
                            id="title"
                            value={jobForm.title}
                            onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                            placeholder="Senior Java Developer"
                            required
                            data-testid="job-title-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="department">Department *</Label>
                          <Input 
                            id="department"
                            value={jobForm.department}
                            onChange={(e) => setJobForm({...jobForm, department: e.target.value})}
                            placeholder="Engineering"
                            required
                            data-testid="job-department-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="location">Location *</Label>
                          <Input 
                            id="location"
                            value={jobForm.location}
                            onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                            placeholder="San Francisco, CA (Remote)"
                            required
                            data-testid="job-location-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="employment_type">Employment Type *</Label>
                          <Select 
                            value={jobForm.employment_type} 
                            onValueChange={(v) => setJobForm({...jobForm, employment_type: v})}
                          >
                            <SelectTrigger data-testid="job-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Full-time">Full-time</SelectItem>
                              <SelectItem value="Contract">Contract</SelectItem>
                              <SelectItem value="Contract-to-Hire">Contract-to-Hire</SelectItem>
                              <SelectItem value="Part-time">Part-time</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="experience_level">Experience Level *</Label>
                          <Input 
                            id="experience_level"
                            value={jobForm.experience_level}
                            onChange={(e) => setJobForm({...jobForm, experience_level: e.target.value})}
                            placeholder="5+ years"
                            required
                            data-testid="job-experience-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="salary_range">Salary Range</Label>
                          <Input 
                            id="salary_range"
                            value={jobForm.salary_range}
                            onChange={(e) => setJobForm({...jobForm, salary_range: e.target.value})}
                            placeholder="$120,000 - $150,000"
                            data-testid="job-salary-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">Description *</Label>
                        <Textarea 
                          id="description"
                          value={jobForm.description}
                          onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                          placeholder="Job description..."
                          rows={4}
                          required
                          data-testid="job-description-input"
                        />
                      </div>

                      {/* Skills */}
                      <div className="space-y-2">
                        <Label>Skills/Tags *</Label>
                        {jobForm.skills.map((skill, index) => (
                          <div key={index} className="flex gap-2">
                            <Input 
                              value={skill}
                              onChange={(e) => updateArrayField('skills', index, e.target.value)}
                              placeholder="e.g., Java, Spring Boot"
                              data-testid={`job-skill-input-${index}`}
                            />
                            {jobForm.skills.length > 1 && (
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="icon"
                                onClick={() => removeArrayField('skills', index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => addArrayField('skills')}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Skill
                        </Button>
                      </div>

                      {/* Requirements */}
                      <div className="space-y-2">
                        <Label>Requirements</Label>
                        {jobForm.requirements.map((req, index) => (
                          <div key={index} className="flex gap-2">
                            <Input 
                              value={req}
                              onChange={(e) => updateArrayField('requirements', index, e.target.value)}
                              placeholder="e.g., 5+ years of Java experience"
                              data-testid={`job-requirement-input-${index}`}
                            />
                            {jobForm.requirements.length > 1 && (
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="icon"
                                onClick={() => removeArrayField('requirements', index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => addArrayField('requirements')}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Requirement
                        </Button>
                      </div>

                      {/* Urgent Toggle */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <Label htmlFor="is_urgent" className="font-medium">Mark as Urgent</Label>
                          <p className="text-sm text-slate-500">Highlight this job as urgent/hot</p>
                        </div>
                        <Switch 
                          id="is_urgent"
                          checked={jobForm.is_urgent}
                          onCheckedChange={(checked) => setJobForm({...jobForm, is_urgent: checked})}
                          data-testid="job-urgent-switch"
                        />
                      </div>

                      <DialogFooter>
                        <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-job-btn">
                          {saving ? 'Saving...' : (editingJob ? 'Update Job' : 'Create Job')}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : jobs.length === 0 ? (
                <div className="p-12 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No jobs yet. Create your first job listing.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="jobs-table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>Department</th>
                        <th>Location</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job, index) => (
                        <tr key={job.id} data-testid={`job-row-${index}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#0A2540]">{job.title}</span>
                              {job.is_urgent && (
                                <Badge variant="destructive" className="text-xs">Urgent</Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-slate-600">{job.department}</td>
                          <td className="text-slate-600">{job.location}</td>
                          <td className="text-slate-600">{job.employment_type}</td>
                          <td>
                            <Badge className={job.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                              {job.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleToggleJobActive(job)}
                                title={job.is_active ? 'Deactivate' : 'Activate'}
                                data-testid={`toggle-job-${index}`}
                              >
                                {job.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleEditJob(job)}
                                data-testid={`edit-job-${index}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`delete-job-${index}`}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Job?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete "{job.title}". This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDeleteJob(job.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <div className="bg-white rounded-xl border border-slate-100">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-semibold text-[#0A2540]">Contact Submissions</h2>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : submissions.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No submissions yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="submissions-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Type</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((sub, index) => (
                        <tr key={sub.id} data-testid={`submission-row-${index}`}>
                          <td className="text-sm text-slate-500">{formatDate(sub.created_at)}</td>
                          <td className="font-medium text-[#0A2540]">{sub.name}</td>
                          <td className="text-slate-600">{sub.email}</td>
                          <td>
                            <Badge variant="outline">{sub.form_type}</Badge>
                          </td>
                          <td className="text-slate-600 max-w-[200px] truncate">{sub.subject}</td>
                          <td>
                            <Badge className={statusColors[sub.status]}>
                              {sub.status}
                            </Badge>
                          </td>
                          <td>
                            <Select 
                              value={sub.status} 
                              onValueChange={(v) => handleUpdateSubmissionStatus(sub.id, v)}
                            >
                              <SelectTrigger className="w-32" data-testid={`update-submission-status-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-white rounded-xl border border-slate-100">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-semibold text-[#0A2540]">Job Applications</h2>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : applications.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No applications yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="applications-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Applicant</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Job ID</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((app, index) => (
                        <tr key={app.id} data-testid={`application-row-${index}`}>
                          <td className="text-sm text-slate-500">{formatDate(app.created_at)}</td>
                          <td className="font-medium text-[#0A2540]">{app.name}</td>
                          <td className="text-slate-600">{app.email}</td>
                          <td className="text-slate-600">{app.phone}</td>
                          <td className="text-slate-600 font-mono text-xs">{app.job_id.slice(0, 8)}...</td>
                          <td>
                            <Badge className={statusColors[app.status]}>
                              {app.status}
                            </Badge>
                          </td>
                          <td>
                            <Select 
                              value={app.status} 
                              onValueChange={(v) => handleUpdateApplicationStatus(app.id, v)}
                            >
                              <SelectTrigger className="w-32" data-testid={`update-application-status-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="reviewing">Reviewing</SelectItem>
                                <SelectItem value="interviewed">Interviewed</SelectItem>
                                <SelectItem value="hired">Hired</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
